import React from "react";

import { cn } from "@/lib/utils";
import { AD_PLACEMENTS, AdPlacementKey } from "@/main/analytics/ads/adUnits";
import {
    areAdsAllowed,
    destroyAdSlot,
    displayAdSlot,
} from "@/main/analytics/ads/googlePublisherTag";
import { CONSENT_UPDATED_EVENT } from "@/main/analytics/consent/cookieConsent";
import { Localizer } from "@/main/react/Types";

type AdSlotProps = {
    placement: AdPlacementKey;
    localizer: Localizer;
    /** Disambiguates repeated slots of the same placement into unique element ids. */
    index?: number;
    className?: string;
};

/**
 * A single Google Ad Manager display slot.
 *
 * UX guarantees (see designs/04-live/2026-06-22-google-ad-manager-display-ads-design.md):
 *  - renders a fixed-height reserved box BEFORE the ad fills → no layout shift (CLS);
 *  - only requests the ad when it scrolls near the viewport (IntersectionObserver) → no off-screen
 *    loads, high viewability;
 *  - returns null (reserving nothing) when ads are disabled or "ads" consent is not granted;
 *  - tears its GPT slot down on unmount so soft navigation never leaks slots.
 */
export function AdSlot({ placement, localizer, index = 0, className }: AdSlotProps) {
    const definition = AD_PLACEMENTS[placement];
    const elementId = `div-gpt-${placement}-${index}`;
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const displayedRef = React.useRef(false);
    const [allowed, setAllowed] = React.useState<boolean>(() => areAdsAllowed());

    // Re-evaluate eligibility when the visitor's consent changes.
    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const handler = () => setAllowed(areAdsAllowed());
        window.addEventListener(CONSENT_UPDATED_EVENT, handler);
        return () => window.removeEventListener(CONSENT_UPDATED_EVENT, handler);
    }, []);

    // Load the ad once the reserved box approaches the viewport.
    React.useEffect(() => {
        if (!allowed || displayedRef.current) {
            return;
        }
        const node = containerRef.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            return;
        }

        const observer = new IntersectionObserver(
            (entries, obs) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && !displayedRef.current) {
                        displayedRef.current = true;
                        displayAdSlot(elementId, placement);
                        obs.disconnect();
                        break;
                    }
                }
            },
            { rootMargin: "600px 0px" },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [allowed, elementId, placement]);

    // Destroy the GPT slot on unmount.
    React.useEffect(() => {
        return () => {
            if (displayedRef.current) {
                destroyAdSlot(elementId);
                displayedRef.current = false;
            }
        };
    }, [elementId]);

    if (!definition || !allowed) {
        return null;
    }

    return (
        <div className={cn("w-full", className)} data-playwright-id={`ad-slot-${placement}`}>
            <div
                ref={containerRef}
                className={cn(
                    "flex w-full flex-col overflow-hidden rounded-xl border border-border bg-card",
                    definition.reservedClassName,
                )}
            >
                <span className="self-start px-3 pt-2 text-[0.625rem] font-medium uppercase tracking-wide text-muted-foreground">
                    {localizer("ads.label")}
                </span>
                <div id={elementId} className="flex w-full flex-1 items-center justify-center" />
            </div>
        </div>
    );
}
